
// src/data/tierConfig.js
// Single source of truth for what each plan tier can access.

const TIER_CONFIG = {
  free: {
    companies: ['Apple', 'Microsoft Corporation'],
    maxQuarters: 1,       // latest quarter only
    visualisations: [
      'Returns on Capital',
      'Profitability Cascade',
    ],
  },
  pro: {
    companies: null,      // null = all companies
    maxQuarters: 6,
    visualisations: null, // null = all visualisations
  },
  premium: {
    companies: null,      // null = all companies
    maxQuarters: 8,
    visualisations: null, // null = all visualisations
  },
};

/**
 * Return the allowed company list for a given tier.
 * Falls back to free-tier restrictions if tier is unknown/null.
 */
export function getAllowedCompanies(planTier, allCompanies) {
  const config = TIER_CONFIG[planTier] || TIER_CONFIG.free;
  if (!config.companies) return allCompanies; // null = unrestricted
  return allCompanies.filter((c) => config.companies.includes(c));
}

/**
 * Return the max quarters allowed for a given tier.
 */
export function getMaxQuarters(planTier) {
  const config = TIER_CONFIG[planTier] || TIER_CONFIG.free;
  return config.maxQuarters;
}

/**
 * Return the allowed visualisation groups for a given tier.
 * Falls back to free-tier restrictions if tier is unknown/null.
 */
export function getAllowedVisualisations(planTier, allGroups) {
  const config = TIER_CONFIG[planTier] || TIER_CONFIG.free;
  if (!config.visualisations) return allGroups; // null = unrestricted
  return config.visualisations;
}

/**
 * Check if a specific visualisation is locked for a tier.
 */
export function isVisualisationLocked(planTier, groupName) {
  const config = TIER_CONFIG[planTier] || TIER_CONFIG.free;
  if (!config.visualisations) return false; // null = all allowed
  return !config.visualisations.includes(groupName);
}

/**
 * Map visualisation group names to definition keys for tier-based blur.
 */
const VIZ_GROUP_TO_DEF_KEY = {
  'Returns on Capital': 'vizReturnsOnCapital',
  'Growth Trajectory Pulse': 'vizGrowthTrajectoryPulse',
  'FCF vs Net Income': 'vizFcfVsNetIncome',
  'Debt & Coverage Profile': 'vizDebtCoverageProfile',
  'Profitability Cascade': 'vizProfitabilityCascade',
  'Liquidity & Solvency': 'vizLiquiditySolvency',
  'Efficiency': 'vizEfficiency',
  'Equity & Book Value': 'vizEquityBookValue',
  'CFO Strength': 'vizCfoStrength',
  'Cash Flow Allocation': 'vizCashFlowAllocation',
};

/**
 * Return the set of definition keys that are locked for a tier.
 */
export function getLockedDefinitionKeys(planTier) {
  const config = TIER_CONFIG[planTier] || TIER_CONFIG.free;
  if (!config.visualisations) return new Set(); // premium = nothing locked
  const locked = new Set();
  for (const [group, key] of Object.entries(VIZ_GROUP_TO_DEF_KEY)) {
    if (!config.visualisations.includes(group)) {
      locked.add(key);
    }
  }
  return locked;
}

export default TIER_CONFIG;
